<template>
    <div :class="computedColClass">
        <div :class="otherClass">

            <!-- LABEL -->
            <label v-if="title" :for="inputId" :class="labelClass">
                <slot name="title">{{ toTitleCase(title) }}</slot>
                <sup v-if="req" :class="spanClass">*</sup>
            </label>

            <!-- INPUT GROUP -->
            <div :class="wrapperClass">
                <input
                    ref="datepicker"
                    :id="inputId"
                    :name="field"
                    type="text"
                    autocomplete="off"
                    :value="modelValue"
                    :readonly="readonly"
                    :disabled="disabled"
                    :placeholder="placeholder ? placeholder : 'Select Date'"
                    :class="computedInputClass"
                    :style="validationStyle"
                    @click.prevent="openPicker"
                />

                <div :class="iconClass">
                    <i class="fa-regular fa-calendar-days"></i>
                </div>
            </div>

            <!-- ERROR MESSAGE -->
            <Error
                v-if="hasError"
                :id="errorId"
                :message="errorMessage"
                :bootstrap="bootstrap"
            />

        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useValidationStore } from '../../stores/validationStore.js';

// jQuery UI
import '@/assets/admin/public/datepicker/jquery-ui.css';
import '@/assets/admin/public/datepicker/jquery-ui.js';

const props = defineProps({
    modelValue: { type: String, default: '' },
    id: { type: String },
    title: { type: String, default: '' },
    field: { type: String, required: true },
    col: { type: String, default: '' },
    otherClass: { type: String, default: 'mb-3' },
    req: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    dateFormat: { type: String, default: 'd MM, yy' },
    changeMonth: { type: Boolean, default: true },
    changeYear: { type: Boolean, default: true },
    yearRange: { type: String, default: '-100:+5' },
    placeholder: { type: String, default: 'Select Date' },
    bootstrap: { type: Boolean, default: false },
    disableFutureDates: { type: Boolean, default: false },
    disablePastDates: { type: Boolean, default: false },
    // Bounds relative to another field, e.g. an end date that cannot fall before
    // its start date. Accepts anything Date can parse, or a jQuery UI offset
    // ('+1d', 0). Reactive: changing the source date re-limits this picker.
    minDate: { type: [String, Number, Date], default: null },
    maxDate: { type: [String, Number, Date], default: null },
});

const emit = defineEmits(['update:modelValue']);
const validationStore = useValidationStore();
const datepicker = ref(null);
let $picker = null;

// Computed IDs
const inputId = computed(() => props.id || `date-${props.field.split('.').pop()}`);
const errorId = computed(() => `error-${inputId.value}`);

// Field for validation
const normalizedField = computed(() => {
    if (!props.field) return '';
    const shortKey = props.field.split('.').pop();
    if (validationStore.hasError(props.field)) return props.field;
    if (validationStore.hasError(shortKey)) return shortKey;
    return props.field;
});

// Error handling
const errorMessage = computed(() => validationStore.getErrors(normalizedField.value));
const hasError = computed(() => validationStore.hasError(normalizedField.value));

// Col class
const computedColClass = computed(() => props.col || (props.bootstrap ? 'col-12 col-md-4 col-lg-3' : 'col-span-12 md:col-span-6 lg:col-span-4'));

// Label and span class
const labelClass = computed(() => props.bootstrap ? 'form-label' : 'block text-sm font-medium text-gray-700 mb-1');
const spanClass = computed(() => props.bootstrap ? 'text-danger' : 'text-red-500');

// Input wrapper
const wrapperClass = computed(() => props.bootstrap ? 'input-group position-relative' : 'relative flex items-center');

// Icon class
const iconClass = computed(() => props.bootstrap ? 'cale_icon position-absolute top-1/2 end-0 translate-middle-y me-3' : 'absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400');

// Input class
const computedInputClass = computed(() => {
    const base = props.bootstrap
        ? 'form-control form-control-sm date-input'
        : 'block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-green-200 focus:border-green-200';
    const error = hasError.value ? (props.bootstrap ? 'is-invalid' : 'border-red-500') : '';
    const success = !hasError.value && props.req && props.modelValue ? (props.bootstrap ? 'border-success' : 'border-green-500') : '';
    return [base, error, success].join(' ');
});

// Validation style for non-bootstrap
const validationStyle = computed(() => hasError.value && !props.bootstrap ? 'outline: 1px solid red !important;' : '');

// An explicit bound wins over the coarse past/future flags. Returned as a Date
// so jQuery UI treats it as an absolute limit rather than a day offset.
const toPickerDate = (value) => {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'number' || value instanceof Date) return value;
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? value : parsed;
};

const effectiveMin = computed(() => {
    const explicit = toPickerDate(props.minDate);
    if (explicit !== null) return explicit;
    return props.disablePastDates ? 0 : null;
});

const effectiveMax = computed(() => {
    const explicit = toPickerDate(props.maxDate);
    if (explicit !== null) return explicit;
    return props.disableFutureDates ? 0 : null;
});

// Open picker
const openPicker = () => {
    if ($picker && !props.disabled) $picker.datepicker('show');
};

// jQuery datepicker init
onMounted(() => {
    if (!datepicker.value) return;
    const options = {
        showOn: 'both',
        dateFormat: props.dateFormat,
        changeMonth: props.changeMonth,
        changeYear: props.changeYear,
        yearRange: props.yearRange,
        onSelect: date => emit('update:modelValue', date),
        minDate: effectiveMin.value,
        maxDate: effectiveMax.value
    };
    $picker = $(datepicker.value).datepicker(options);

    // Keep the limits in step with the field they depend on. Without this the
    // picker keeps whatever bound it had at mount, and an end date could still
    // be chosen before its start date.
    watch([effectiveMin, effectiveMax], ([min, max]) => {
        if (!$picker) return;
        $picker.datepicker('option', { minDate: min, maxDate: max });
    });

    // Close on outside click
    // $(document).on('click.datePicker', e => {
    //     if (!$(e.target).closest('.date-input, .ui-datepicker').length) {
    //         $picker?.datepicker('hide');
    //     }
    // });
});

onBeforeUnmount(() => {
    if ($picker) {
        $picker.datepicker('destroy');
        $picker = null;
    }
    $(document).off('click.datePicker');
});

// Utility
const toTitleCase = str => {
    if (!str) return '';
    return str.replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
};
</script>

<style scoped>
.date-input,
.date-input:disabled {
    background: rgb(233, 236, 239);
    opacity: 1;
    cursor: pointer;
}

.date {
    background: #fff;
}

.cale_icon {
    pointer-events: none;
    z-index: 1;
    top: 50%;
}

.ui-datepicker {
    z-index: 9999 !important;
}
</style>
